import type { ComponentType } from 'react'
import { useAuthContext } from '@offer/auth-lib'
import { useEffect, useMemo, useRef } from 'react'
import { AccessDeniedPage } from '@/components/accessDenied'
import { Loading } from '@/components/loading/Loading'
import { MaintenancePage } from '@/components/maintenance'
import { useScrollContext } from '@/contexts/scroll/useScrollContext'
import { useParameters } from '@/hooks/useParameters'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'

export function AuthenticatedApp({
  Component,
}: {
  readonly Component: ComponentType
}) {
  const { user } = useAuthContext()
  const userId = user?.id

  const { data, isLoading, isError } = useParameters(userId)

  const { isUnderMaintenance, isAppLaunched, hasAppAccess } = useMemo(() => {
    return {
      isUnderMaintenance: data?.some(param => param.parameter_name === 'UNDER_MAINTENANCE' && param.parameter_value === 'true'),
      isAppLaunched: data?.some(param => param.parameter_name === 'APP_LAUNCHED' && param.parameter_value === 'true'),
      hasAppAccess: data?.some(param => param.parameter_name === 'APP_ACCESS' && param.parameter_value === 'true'),
    }
  }, [data])

  const { toggleHeaderVisibility } = useScrollContext()

  const isAccessDenied = isError || (isAppLaunched === false && hasAppAccess === false)
  const accessDeniedStartedAtRef = useRef<number | null>(null)

  useEffect(() => {
    toggleHeaderVisibility(isAccessDenied)

    return () => toggleHeaderVisibility(false)
  }, [isAccessDenied, toggleHeaderVisibility])

  useEffect(() => {
    if (isAccessDenied && accessDeniedStartedAtRef.current === null) {
      accessDeniedStartedAtRef.current = performance.now()
      trackAnalyticsEvent(ANALYTICS_EVENTS.ACCESS_DENIED_PAGE_VIEWED)
    }

    if (!isAccessDenied && accessDeniedStartedAtRef.current !== null) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.ACCESS_DENIED_PAGE_EXITED, {
        duration_ms: Math.round(
          performance.now() - accessDeniedStartedAtRef.current,
        ),
      })
      accessDeniedStartedAtRef.current = null
    }
  }, [isAccessDenied])

  useEffect(() => () => {
    if (accessDeniedStartedAtRef.current === null) {
      return
    }

    trackAnalyticsEvent(ANALYTICS_EVENTS.ACCESS_DENIED_PAGE_EXITED, {
      duration_ms: Math.round(
        performance.now() - accessDeniedStartedAtRef.current,
      ),
    })
    accessDeniedStartedAtRef.current = null
  }, [])

  if (
    isLoading
    || isError
    || (!hasAppAccess && hasAppAccess === undefined)
    || (!isAppLaunched && isAppLaunched === undefined)
  ) {
    return <Loading />
  }

  if (isAccessDenied) {
    return <AccessDeniedPage />
  }

  return isUnderMaintenance === true ? <MaintenancePage /> : <Component />
}

export default AuthenticatedApp
